import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './dashShared.css';
import './PipelineMain.css';

import { Overview }       from './Pipeline_Elements/Overview';
import { PipelineTrend }  from './Pipeline_Elements/PipelineTrend';
import { DealMovement }   from './Pipeline_Elements/DealMovement';
import { RepKpis }        from './Pipeline_Elements/RepKpis';
import { BusinessUnit }   from './Pipeline_Elements/BusinessUnit';
import { DealExplorer }   from './Pipeline_Elements/DealExplorer';
import { PipelineMaster } from './Pipeline_Elements/PipelineMaster';
import { RepDeepDive }    from './Pipeline_Elements/RepDeepDive';
import { FPA }            from './Pipeline_Elements/FPA';
import { StageStall }     from './Pipeline_Elements/StageStall';
import { IndustryBenchmark } from './Pipeline_Elements/IndustryBenchmark';
import { Insights }       from './Pipeline_Elements/Insights';
import { DevOverlay }     from '../components/DevOverlay/DevOverlay';
import { DashboardLoader } from '../components/DashboardLoader/DashboardLoader';
import { DashboardAIChat } from '../components/DashboardAIChat/DashboardAIChat';
import { readApiJson } from '../utils/apiErrors';
import { API_URL } from '../config/api';

const TABS = [
  { id: 'exec',      label: '📊 Overview',                   dot: '#1877f2', component: 'Overview' },
  { id: 'trend',     label: '📈 Pipeline Trend',             dot: '#0891b2', component: 'PipelineTrend' },
  { id: 'movement',  label: '🔄 Deal Movement',              dot: '#7c3aed', component: 'DealMovement' },
  { id: 'reps',      label: '👥 Rep KPIs',                   dot: '#059669', component: 'RepKpis' },
  { id: 'repdetail', label: '🎯 Rep Deep Dive',              dot: '#0d9488', component: 'RepDeepDive' },
  { id: 'bu',        label: '🌍 BU & Source',                dot: '#c2410c', component: 'BusinessUnit' },
  { id: 'fpa',       label: '💼 FP&A',                       dot: '#4f46e5', component: 'FPA' },
  { id: 'aging',     label: '⏱ Stage Stall Intelligence',    dot: '#dc2626', component: 'StageStall' },
  { id: 'benchmark', label: '📐 Industry Benchmark',         dot: '#7c3aed', component: 'IndustryBenchmark' },
  { id: 'explorer',  label: '📋 Deal Explorer',              dot: '#dc2626', component: 'DealExplorer' },
  { id: 'commentary', label: '📝 Insights',                  dot: '#0891b2', component: 'Insights' },
  { id: 'pl_master', label: 'Pipeline Master',               dot: '#6d28d9', component: 'PipelineMaster' },
];

const EMPTY_FILTERS = {
  team: '', owner: '', stage: '', forecast: '', bu: '', order_type: '',
  quarter: '', year: '',
};

const FILTER_DEFS = [
  { key: 'team', label: 'Team', empty: 'All Teams' },
  { key: 'owner', label: 'Rep', empty: 'All Reps' },
  { key: 'stage', label: 'Stage', empty: 'All Stages' },
  { key: 'forecast', label: 'Forecast', empty: 'All Forecasts' },
  { key: 'bu', label: 'BU', empty: 'All BUs' },
  { key: 'order_type', label: 'Type', empty: 'All Types' },
  { key: 'quarter', label: 'Quarter', empty: 'All Quarters' },
];

// Short display labels for values that would otherwise show their raw,
// wordier form verbatim (e.g. team's raw "Sales - N.A. Direct" -> "NA
// Direct"). Anything not listed here just displays as its raw value -- this
// only prettifies known values, it never hides one that isn't in the map.
const DYNAMIC_LABEL_OVERRIDES = {
  team: { 'Sales - N.A. Direct': 'NA Direct', 'Sales - APJ Direct': 'APJ Direct' },
  stage: {
    '5% - Prospecting': '5% Prospecting', '20%-Discovery': '20% Discovery',
    '40%-Scoping': '40% Scoping', '60%-Propose': '60% Propose',
    '80%-Validate': '80% Validate', '90%-Negotiate & Close': '90% Negotiate',
  },
  forecast: { 'Not forecasted': 'Not Forecasted', 'Closed won': 'Closed Won' },
};

// Every filter option list is built from whatever values actually appear in
// the loaded deals instead of a hardcoded list -- a fixed list silently
// drops anything new (this is exactly how the Robert Sokolowski casing bug,
// the Akshay Garg/Sanjay Khandkar quota-roster gap, and Overview.js's own
// "Upside became Likely + Best Case" forecast-category drift all happened;
// see Overview.js's identical note on that last one). Stage and Forecast
// still get a meaningful business order instead of alphabetical (via
// `rank` below) -- that's presentation, not a second hardcoded source of
// truth, so a genuinely new stage/forecast value still shows up (just
// sorted after the known ones) rather than disappearing.
const DYNAMIC_FILTER_FIELDS = {
  owner: 'owner', team: 'team', bu: 'bu',
  stage: 'stage', forecast: 'forecast_category', order_type: 'order_type',
};

const STAGE_RANK_ORDER = [
  '5% - Prospecting', '20%-Discovery', '40%-Scoping', '60%-Propose',
  '80%-Validate', '90%-Negotiate & Close', 'Business Won', 'Business Lost',
];
const stageRank = value => {
  const i = STAGE_RANK_ORDER.indexOf(value);
  return i === -1 ? STAGE_RANK_ORDER.length : i;
};
// Same "Commit leads, Not Forecasted trails, everything else (Upside,
// Likely, Best Case, or whatever the CRM currently calls it) sits between"
// rule as Overview.js's forecastGroups, so the filter dropdown and the
// breakdown cards it's filtering always agree on ordering.
const forecastRank = value => {
  const v = value.trim().toLowerCase();
  if (v === 'commit') return -1;
  if (v === '' || v === 'not forecasted') return 1;
  return 0;
};

function dynamicOptionsFor(key, deals, rank) {
  const field = DYNAMIC_FILTER_FIELDS[key];
  const overrides = DYNAMIC_LABEL_OVERRIDES[key] || {};
  const values = new Set();
  (deals || []).forEach(deal => {
    const value = (deal[field] || '').trim();
    if (value) values.add(value);
  });
  const sorted = [...values].sort((a, b) => (rank ? rank(a) - rank(b) : 0) || a.localeCompare(b));
  return sorted.map(value => [value, overrides[value] || value]);
}

// close_quarter values look like "Q1 26" / "Q3 27+" -- parsed for
// chronological sort and for deriving which years actually have data,
// instead of a hardcoded quarter/year list that goes stale every fiscal
// year (or the moment a "27+" style catch-all bucket is renamed).
function parseQuarter(value) {
  const m = /^Q(\d)\s*(\d{2})(\+)?$/i.exec((value || '').trim());
  return m ? { quarter: Number(m[1]), year: m[2], plus: !!m[3] } : null;
}

function dynamicQuarterOptions(deals) {
  const values = new Set();
  (deals || []).forEach(deal => {
    const value = (deal.close_quarter || '').trim();
    if (value && parseQuarter(value)) values.add(value);
  });
  return [...values].sort((a, b) => {
    const pa = parseQuarter(a), pb = parseQuarter(b);
    return pa.year - pb.year || pa.quarter - pb.quarter || (pa.plus === pb.plus ? 0 : pa.plus ? 1 : -1);
  });
}

function dynamicYearOptions(deals) {
  const years = new Set();
  (deals || []).forEach(deal => {
    const parsed = parseQuarter(deal.close_quarter);
    if (parsed) years.add(parsed.year);
  });
  return [...years].sort();
}

const fmtFilterMoney = value => {
  const amount = Number(value) || 0;
  if (amount === 0) return '$0';
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(2)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(0)}K`;
  return `$${Math.round(amount).toLocaleString()}`;
};

function Pipeline({ user, onDataLoaded }) {
  const [tab,       setTab]       = useState('exec');
  const [selectedRep, setSelectedRep] = useState(null);
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const [viewMode, setViewMode] = useState('unweighted');
  const fileRef = useRef(null);
  const canManageData = user?.isSuperuser || user?.access?.includes('data_manager');

  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    params.set('view_mode', viewMode);
    fetch(`${API_URL}/api/pipeline/?${params}`, { credentials: 'include' })
      .then(r => readApiJson(r, 'Pipeline dashboard API unavailable. Check Django is running on port 8000.'))
      .then(d => { setData(d); setLoading(false); onDataLoaded?.(); })
      .catch(error => { setImportMsg(error.message); setLoading(false); });
  }, [filters, viewMode, onDataLoaded]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchWeekDeals = useCallback((week, { includeZero = false } = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    params.set('view_mode', viewMode);
    params.set('week', week);
    // Every other deals fetch wants amount > 0 rows only (the default), but
    // the "no value deals" breakdown specifically needs the zero-amount
    // rows the API otherwise always excludes.
    if (includeZero) params.set('include_zero', '1');
    return fetch(`${API_URL}/api/pipeline/?${params}`, { credentials: 'include' })
      .then(r => readApiJson(r, 'Pipeline dashboard API unavailable. Check Django is running on port 8000.'))
      .then(d => d.deals || []);
  }, [filters, viewMode]);

  const handleImport = useCallback((file, mode = 'replace') => {
    if (!file || !canManageData) return Promise.resolve({ ok: false, message: 'Not authorized to import data.' });
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mode', mode);
    setImporting(true);
    setImportMsg('');
    // Returns the outcome so callers (e.g. PipelineMaster's import modal) can
    // show a real success/failure result tied to the upload, instead of only
    // relying on the importMsg shown in the sub-nav corner.
    return fetch(`${API_URL}/api/pipeline/import/`, { method: 'POST', body: fd, credentials: 'include' })
      .then(r => readApiJson(r, 'Upload failed.'))
      .then(d => {
        setImportMsg(d.message || 'Done');
        setImporting(false);
        if (d.message) fetchData();
        return { ok: true, message: d.message || 'Done' };
      })
      .catch(error => {
        setImportMsg(error.message);
        setImporting(false);
        return { ok: false, message: error.message };
      });
  }, [canManageData, fetchData]);

  const setFilter = (key, val) => setFilters(current => {
    if (key !== 'year') return { ...current, [key]: val };
    const quarterMatchesYear = !current.quarter
      || (val === '26' && current.quarter.endsWith(' 26'))
      || (val === '27' && (current.quarter.endsWith(' 27') || current.quarter === 'Q3 27+'));
    return { ...current, year: val, quarter: !val || quarterMatchesYear ? current.quarter : '' };
  });
  const clearFilters = () => setFilters(EMPTY_FILTERS);
  const hasFilters = Object.values(filters).some(Boolean);
  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const openRep = useCallback((owner) => {
    setSelectedRep(owner || null);
    setTab('repdetail');
  }, []);
  const openMovement = useCallback(() => setTab('movement'), []);

  const optionsFor = useCallback(key => {
    if (key === 'quarter') {
      const quarters = dynamicQuarterOptions(data?.deals);
      if (filters.year) return quarters.filter(value => parseQuarter(value).year === filters.year);
      return quarters;
    }
    if (key === 'stage') return dynamicOptionsFor(key, data?.deals, stageRank);
    if (key === 'forecast') return dynamicOptionsFor(key, data?.deals, forecastRank);
    if (DYNAMIC_FILTER_FIELDS[key]) return dynamicOptionsFor(key, data?.deals);
    return [];
  }, [filters.year, data]);
  const filterResult = useMemo(() => ({
    deals: data?.kpis?.active_deals || 0,
    amount: data?.kpis?.active_pipeline || 0,
  }), [data]);

  /* ── Loading ── */
  if (loading && !data) return (
    <div className="dash-shell">
      <div className="dash-loading">
        <DashboardLoader label="Preparing pipeline dashboard..." />
        Loading pipeline data…
      </div>
    </div>
  );

  /* ── Empty state ── */
  if (!data?.has_data) return (
    <div className="dash-shell">
      <div className="dash-empty">
        <div className="dash-empty-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#1877f2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
        </div>
        <h2>Pipeline Intelligence</h2>
        <p>{canManageData ? 'No pipeline data yet. Upload your Pipeline Database Excel file to get started.' : 'No pipeline data has been loaded. Please contact a Data Manager.'}</p>
        {canManageData && <button className="dash-empty-btn" onClick={() => fileRef.current?.click()} disabled={importing}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          {importing ? 'Importing…' : 'Upload Pipeline Excel'}
        </button>}
        <input ref={fileRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleImport(f); }} />
        {importMsg && <p className="dash-import-msg">{importMsg}</p>}
      </div>
    </div>
  );

  return (
    <div className="dash-shell pl">

      {/* ── Sub-navigation ── */}
      <DevOverlay name="TabNavigation">
        <div className="dash-subnav">
          <div className="dash-tabs pipeline-tabs-scroll">
            {TABS.filter(t => canManageData || t.id !== 'pl_master').map(t => (
              <button
                key={t.id}
                data-component={t.component}
                className={`dash-tab${tab === t.id ? ' active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                <span
                  className="dash-tab-dot"
                  style={{ background: tab === t.id ? t.dot : 'transparent', border: `2px solid ${t.dot}` }}
                />
                {t.label}
              </button>
            ))}
          </div>
          <div className="dash-subnav-actions">
            {importMsg && <span className="dash-import-inline-msg">{importMsg}</span>}
            {/* <button
              className={`dash-import-btn${importing ? ' loading' : ''}`}
              onClick={() => !importing && fileRef.current?.click()}
              disabled={importing}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              {importing ? 'Importing…' : 'Import Data'}
            </button> */}
            <input ref={fileRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleImport(f); }} />
          </div>
        </div>
      </DevOverlay>

      {/* ── Filter bar ── */}
      <DevOverlay name="FilterBar">
        <>
        <div className="dash-filterbar pipeline-filterbar" aria-busy={loading}>
          <span className="pipeline-filter-label">Filters</span>
          {FILTER_DEFS.map(({ key, label, empty }) => (
            <label key={key} className="pipeline-filter-control">
              <span className="sr-only">{label}</span>
              <select
                className="pipeline-filter-select"
                value={filters[key]}
                onChange={e => setFilter(key, e.target.value)}
                aria-label={label}
              >
                <option value="">{empty}</option>
                {optionsFor(key).map(option => {
                  const [value, text] = Array.isArray(option) ? option : [option, option];
                  return <option key={value} value={value}>{text}</option>;
                })}
              </select>
            </label>
          ))}
          <span className="pipeline-filter-label pipeline-year-label">📅 Year</span>
          <label className="pipeline-filter-control">
            <span className="sr-only">Year</span>
            <select
              className="pipeline-filter-select pipeline-year-select"
              value={filters.year}
              onChange={e => setFilter('year', e.target.value)}
              aria-label="Year"
            >
              <option value="">All Years</option>
              {dynamicYearOptions(data?.deals).map(yr => <option key={yr} value={yr}>{`FY 20${yr}`}</option>)}
            </select>
          </label>
          <span className="pipeline-filter-divider" />
          <span className="pipeline-filter-label pipeline-view-label">📊 View</span>
          <span className="pipeline-view-toggle" role="group" aria-label="Pipeline value view">
            {['unweighted', 'weighted'].map(mode => (
              <button
                key={mode}
                type="button"
                className={`pipeline-view-button${viewMode === mode ? ' active' : ''}`}
                aria-pressed={viewMode === mode}
                onClick={() => setViewMode(mode)}
              >
                {mode === 'unweighted' ? 'Unweighted' : 'Weighted'}
              </button>
            ))}
          </span>
          <span className="pipeline-filter-divider" />
          <button type="button" className="pipeline-filter-reset" onClick={clearFilters}>↺ Reset</button>
          {hasFilters && <span className="pipeline-filter-status">({activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'} active)</span>}
        </div>
        {hasFilters && (
          <div className="pipeline-filter-banner">
            <span>
              🔍 {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'} active
              {filters.year ? ` · 📅 FY20${filters.year}` : ''}
              {' — '}{filterResult.deals} deals · {fmtFilterMoney(filterResult.amount)}
            </span>
            <button type="button" onClick={clearFilters}>✕ Clear All Filters</button>
          </div>
        )}
        </>
      </DevOverlay>

      {/* ── Content ── */}
      <div className="dash-content pl-body">
        {tab === 'exec' && (
          <DevOverlay name="Overview">
            <Overview data={data} onNavigate={setTab} fetchWeekDeals={fetchWeekDeals} />
          </DevOverlay>
        )}
        {tab === 'trend' && (
          <DevOverlay name="PipelineTrend">
            <PipelineTrend data={data} fetchWeekDeals={fetchWeekDeals} />
          </DevOverlay>
        )}
        {tab === 'movement' && (
          <DevOverlay name="DealMovement">
            <DealMovement data={data} />
          </DevOverlay>
        )}
        {tab === 'reps' && (
          <DevOverlay name="RepKPIs">
            <RepKpis data={data} onSelectRep={openRep} fetchWeekDeals={fetchWeekDeals} />
          </DevOverlay>
        )}
        {tab === 'bu' && (
          <DevOverlay name="BU">
            <BusinessUnit data={data} />
          </DevOverlay>
        )}
        {tab === 'repdetail' && (
          <DevOverlay name="RepDeepDive">
            <RepDeepDive data={data} initialRep={selectedRep} />
          </DevOverlay>
        )}
        {tab === 'explorer' && (
          <DevOverlay name="DealExplorer">
            <DealExplorer data={data} />
          </DevOverlay>
        )}
        {tab === 'fpa' && (
          <DevOverlay name="FPA">
            <FPA data={data} onSelectRep={openRep} fetchWeekDeals={fetchWeekDeals} />
          </DevOverlay>
        )}
        {tab === 'aging' && (
          <DevOverlay name="StageStall">
            <StageStall data={data} onSelectRep={openRep} />
          </DevOverlay>
        )}
        {tab === 'benchmark' && (
          <DevOverlay name="IndustryBenchmark">
            <IndustryBenchmark data={data} fetchWeekDeals={fetchWeekDeals} />
          </DevOverlay>
        )}
        {tab === 'commentary' && (
          <DevOverlay name="Insights">
            <Insights data={data} onOpenMovement={openMovement} />
          </DevOverlay>
        )}
        {canManageData && tab === 'pl_master' && (
          <DevOverlay name="PipelineMaster">
            <PipelineMaster data={data} handleImport={handleImport} importing={importing} canManageData={canManageData} />
          </DevOverlay>
        )}
      </div>
      <DashboardAIChat dashboard="pipeline" activeTab={tab} filters={{ ...filters, view_mode: viewMode }} />
    </div>
  );
}

export default Pipeline;
